import { Funds } from './style';
import { useState } from 'react';
import { outputDataService } from '../../../../services/outputData.service copy';
import { useNavigate } from 'react-router-dom';
import { useLoadDataToSnowflake } from '../../../../services/workflow.services';
import { notifications } from '@mantine/notifications';
import { Alert, ActionIcon, Anchor, Flex, Modal, Text, Box, Checkbox, Button } from '@mantine/core';
import { TbArrowUpRight, TbInfoCircle } from 'react-icons/tb';

export function UpdateReportModal({
  id,
  isOpen,
  onClose,
  inputFunds,
  onUpdateReport,
  isLoading,
  periodId,
  workflowId,
  transformationStatus,
  extractionStatus,
  lookupStatus
}) {
  const navigate = useNavigate();
  const [fundIds, setFundIds] = useState([]);
  const [isShowMessage, setIsShowMessage] = useState(true);
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const loadDataToSnowflake = useLoadDataToSnowflake();

  const funds = Array.isArray(inputFunds?.data) ? inputFunds.data : [];

  const onSelectFund = id => {
    if (fundIds.includes(id)) {
      setFundIds(fundIds.filter(val => val !== id));
    } else {
      setFundIds([...fundIds, id]);
    }
  };

  const onSelectAll = () => {
    if (fundIds.length === funds.length) {
      setFundIds([]);
    } else {
      setFundIds(funds.map(item => item.id));
    }
  };

  const loadDataFromSnowflake = async () => {
    loadDataToSnowflake.mutate(periodId, {
      onSuccess: () => {
        onUpdateReport(fundIds, () => {
          setFundIds([]);
          setIsLoadingSubmit(false);
        });
      },
      onError: err => {
        if (err?.data?.details?.error === 'duplicate_columns') {
          notifications.show({
            title: (
              <>
                Your data has duplicated column name from file {err?.data?.file_name} and sheet:{' '}
                {err?.data?.sheet_name}. Remove the duplicated column or rename it{' '}
                <Anchor
                  href={`/data-extraction/${workflowId}/${periodId}?fileId=${err?.data?.file_id}&sheetId=${err?.data?.sheet_id}&tab=transformation`}
                >
                  here
                </Anchor>
                .
              </>
            ),
            color: 'red',
            withBorder: true,
            position: 'bottom-center',
            withCloseButton: true,
            autoClose: 100000
          });
        }
        setIsLoadingSubmit(false);
      }
    });
  };

  const handleCheckSnowflakeData = async () => {
    setIsLoadingSubmit(true);
    outputDataService
      .getOutputData({
        period_id: periodId
      })
      .then(res => {
        const errors = [
          ...res
            .map(category => ({
              category: category.name,
              tables: [
                ...category.tables.map(table => ({
                  name: table.name,
                  errors: [...table.errors.map(error => ({ ...error }))]
                }))
              ]
            }))
            .filter(category => category.tables.filter(table => table.errors.length > 0).length > 0)
        ];

        if (errors?.length > 0) {
          setIsShowErrorMessage(true);
          setIsLoadingSubmit(false);
          return;
        }

        if (
          transformationStatus === 'In progress' ||
          extractionStatus === 'In progress' ||
          lookupStatus === 'In progress'
        ) {
          loadDataFromSnowflake();
          return;
        }

        onUpdateReport(
          fundIds,
          () => {
            setFundIds([]);
            setIsLoadingSubmit(false);
          },
          () => {
            setIsLoadingSubmit(false);
          }
        );
      })
      .catch(err => {
        setIsShowErrorMessage(true);
        setIsLoadingSubmit(false);
      });
  };

  const handleCancel = () => {
    onClose();
    setFundIds([]);
  };

  return (
    <Modal
      id={id ?? 'update-report-modal'}
      opened={isOpen}
      onClose={onClose}
      size="50%"
      centered
      title="Regenerate Report"
    >
      <Box id="update-report-modal-content">
        <Alert
          id="update-report-modal-error-alert"
          p="12px"
          hidden={!isShowErrorMessage}
          variant="light"
          color="red"
          icon={<TbInfoCircle size={16} />}
        >
          <Flex align="center" gap="8px">
            <Text c="red" fz="14px" fw={400}>
              Your recent changes caused errors in extraction/transformation, please fix it before
              regenerating the report
            </Text>
            <ActionIcon
              id="update-report-modal-go-to-output-tables-button"
              variant="subtle"
              color="red"
              onClick={() => navigate(`/output-tables/${workflowId}/${periodId}`)}
            >
              <TbArrowUpRight size={16} />
            </ActionIcon>
          </Flex>
        </Alert>
        <Alert
          id="update-report-modal-info-alert"
          p="12px"
          hidden={!isShowMessage}
          variant="light"
          color="blue"
          icon={<TbInfoCircle size={16} />}
          withCloseButton
          onClose={() => setIsShowMessage(false)}
          mt="12px"
        >
          The report of the selected funds will be overwritten
        </Alert>
        <Box>
          <Funds id="update-report-modal-funds-list">
            <div className="header" id="update-report-modal-funds-header">
              <div className="select-all" id="update-report-modal-select-all">
                <Checkbox
                  id="update-report-modal-select-all-checkbox"
                  onChange={onSelectAll}
                  checked={funds.length > 0 && fundIds.length === funds.length}
                  size="xs"
                />
                All Funds
              </div>
              <p id="update-report-modal-funds-count">{funds.length} fund(s)</p>
            </div>
            {funds.map(item => (
              <div key={item.id} className="item" id={`update-report-modal-fund-item-${item.id}`}>
                <Flex align="center" gap="8px" id={`update-report-modal-fund-item-row-${item.id}`}>
                  <Checkbox
                    id={`update-report-modal-fund-checkbox-${item.id}`}
                    onChange={() => onSelectFund(item.id)}
                    checked={fundIds.includes(item.id)}
                    size="xs"
                  />
                  {item.name}
                </Flex>
              </div>
            ))}
          </Funds>
          <Flex id="update-report-modal-actions" justify="flex-end" gap="12px" mt="16px">
            <Button
              id="update-report-modal-cancel-button"
              size="sm"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              id="update-report-modal-update-button"
              size="sm"
              disabled={fundIds.length === 0 || isLoading || isLoadingSubmit}
              onClick={handleCheckSnowflakeData}
              loading={isLoading || isLoadingSubmit}
            >
              Update Reports {fundIds.length > 0 && `(${fundIds.length})`}
            </Button>
          </Flex>
        </Box>
      </Box>
    </Modal>
  );
}
